import { useMemo, useRef, useState } from 'react';
import { tenantsService, TenantView } from '../../services/tenants/tenants.service';
import { useAdminTenants } from '../../contexts/AdminEventsProvider';
import { useAuth } from '../../contexts/AuthProvider';
import {
  INITIAL_EXPANDED_TENANT_ID,
  INITIAL_TENANT_NAME,
  getTenantsCountLabel,
} from './tenants-page.types';
import {
  addUpdatingTenantId,
  buildCreateTenantPayload,
  buildToggleActivePayload,
  buildToggleTenantImpact,
  normalizeCompanyName,
  removeUpdatingTenantId,
  toggleExpandedTenant,
} from './tenants-page.utils';

// A pending logo edit for one row: a picked file (with local preview) or a staged removal.
type StagedPhotoAction =
  | { kind: 'pick'; file: File; previewUrl: string }
  | { kind: 'remove' };

/**
 * Architectural summary: Encapsulates TenantsPage state + action handlers
 * (create modal flow, activation toggle, row expansion), while consuming the
 * canonical tenants snapshot from AdminEventsProvider.
 */
export function useTenantsPageController() {
  const { tenants, isLoading, error: tenantsError } = useAdminTenants();
  const { refreshAvailableTenants } = useAuth();
  const [companyName, setCompanyName] = useState(INITIAL_TENANT_NAME);
  const [isCreating, setIsCreating] = useState(false);
  const [updatingTenantIds, setUpdatingTenantIds] = useState<number[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [expandedTenantId, setExpandedTenantId] = useState<number | null>(INITIAL_EXPANDED_TENANT_ID);
  const [ownerEmailDrafts, setOwnerEmailDrafts] = useState<Record<number, string>>({});
  const [photoDrafts, setPhotoDrafts] = useState<Record<number, StagedPhotoAction>>({});
  const panelSaveLocksRef = useRef<Set<number>>(new Set());

  const displayedError = error ?? tenantsError;
  const tenantsCountLabel = useMemo(() => getTenantsCountLabel(tenants), [tenants]);

  /**
   * Architectural summary: Submits new tenant from modal and relies on SSE
   * provider snapshot to reflect the canonical row.
   */
  const handleCreateTenant = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const normalizedName = normalizeCompanyName(companyName);
    if (!normalizedName) {
      setError('שם החברה נדרש');
      return;
    }

    setIsCreating(true);
    setError(null);
    try {
      await tenantsService.create(buildCreateTenantPayload(companyName));
      setCompanyName(INITIAL_TENANT_NAME);
      setIsCreateOpen(false);
    } catch (createError: unknown) {
      setError(createError instanceof Error ? createError.message : 'יצירת החברה נכשלה');
    } finally {
      setIsCreating(false);
    }
  };

  /**
   * Architectural summary: Toggles tenant active state, requesting impact
   * details before deactivation to support explicit confirmation.
   */
  const toggleTenantActive = async (tenant: TenantView) => {
    setError(null);

    if (tenant.isActive) {
      try {
        const impact = await tenantsService.deactivationImpact(tenant.id);
        const details = buildToggleTenantImpact(tenant, impact);
        if (!window.confirm([details.title, ...details.lines].join('\n'))) {
          return;
        }
      } catch (impactError: unknown) {
        setError(impactError instanceof Error ? impactError.message : 'שליפת השפעת ההשבתה נכשלה');
        return;
      }
    }

    setUpdatingTenantIds((previous) => addUpdatingTenantId(previous, tenant.id));
    try {
      await tenantsService.updateActive(tenant.id, buildToggleActivePayload(tenant));
    } catch (updateError: unknown) {
      setError(updateError instanceof Error ? updateError.message : 'עדכון סטטוס החברה נכשל');
    } finally {
      setUpdatingTenantIds((previous) => removeUpdatingTenantId(previous, tenant.id));
    }
  };

  // Stages a picked logo with a local preview; nothing hits the network until "שמור הכל".
  const stageTenantPhoto = (tenant: TenantView, file: File | null) => {
    setError(null);
    setPhotoDrafts((current) => {
      const previous = current[tenant.id];
      if (previous?.kind === 'pick') {
        URL.revokeObjectURL(previous.previewUrl);
      }
      if (!file) {
        const next = { ...current };
        delete next[tenant.id];
        return next;
      }
      return {
        ...current,
        [tenant.id]: { kind: 'pick', file, previewUrl: URL.createObjectURL(file) },
      };
    });
  };

  // Stages a logo removal (placeholder preview); the delete only fires on "שמור הכל".
  const stageRemoveTenantPhoto = (tenant: TenantView) => {
    setError(null);
    setPhotoDrafts((current) => {
      const previous = current[tenant.id];
      if (previous?.kind === 'pick') {
        URL.revokeObjectURL(previous.previewUrl);
      }
      return { ...current, [tenant.id]: { kind: 'remove' } };
    });
  };

  const getStagedPhotoFile = (tenant: TenantView): File | null => {
    const staged = photoDrafts[tenant.id];
    return staged?.kind === 'pick' ? staged.file : null;
  };

  // Preview src: staged pick -> local blob, staged removal -> none, else the saved photoUrl.
  const getTenantPhotoPreviewUrl = (tenant: TenantView): string | null => {
    const staged = photoDrafts[tenant.id];
    if (staged?.kind === 'pick') {
      return staged.previewUrl;
    }
    if (staged?.kind === 'remove') {
      return null;
    }
    return tenant.photoUrl ?? null;
  };

  const showRemovePhotoControl = (tenant: TenantView): boolean =>
    Boolean(tenant.photoUrl) && !photoDrafts[tenant.id];

  /**
   * Architectural summary: Opens create modal with clean draft state.
   */
  const openCreateModal = () => {
    setError(null);
    setCompanyName(INITIAL_TENANT_NAME);
    setIsCreateOpen(true);
  };

  /**
   * Architectural summary: Toggles one tenant row expanded state.
   */
  const onToggleExpanded = (tenantId: number) => {
    setExpandedTenantId((current) => toggleExpandedTenant(current, tenantId));
  };

  const setTenantOwnerEmailDraft = (tenantIdPk: number, value: string) => {
    setOwnerEmailDrafts((current) => ({ ...current, [tenantIdPk]: value }));
  };

  const getTenantOwnerEmailDraft = (tenant: TenantView): string => {
    return ownerEmailDrafts[tenant.id] ?? tenant.ownerEmail ?? '';
  };

  const isTenantEmailChanged = (tenant: TenantView): boolean =>
    getTenantOwnerEmailDraft(tenant).trim() !== (tenant.ownerEmail ?? '').trim();

  const hasTenantPanelChanges = (tenant: TenantView): boolean =>
    isTenantEmailChanged(tenant) || Boolean(photoDrafts[tenant.id]);

  // Saves the whole panel in one action: diff-only email PATCH + staged photo upload/removal.
  const saveTenantPanel = async (tenant: TenantView) => {
    if (panelSaveLocksRef.current.has(tenant.id) || !hasTenantPanelChanges(tenant)) {
      return;
    }
    panelSaveLocksRef.current.add(tenant.id);
    setError(null);
    setUpdatingTenantIds((previous) => addUpdatingTenantId(previous, tenant.id));
    try {
      if (isTenantEmailChanged(tenant)) {
        const ownerEmailRaw = getTenantOwnerEmailDraft(tenant).trim();
        await tenantsService.updateOwnerEmail(tenant.id, {
          ownerEmail: ownerEmailRaw.length > 0 ? ownerEmailRaw : null,
        });
      }
      const staged = photoDrafts[tenant.id];
      if (staged?.kind === 'pick') {
        await tenantsService.uploadPhoto(tenant.id, staged.file);
        URL.revokeObjectURL(staged.previewUrl);
      } else if (staged?.kind === 'remove') {
        await tenantsService.removePhoto(tenant.id);
      }
      setPhotoDrafts((current) => {
        const next = { ...current };
        delete next[tenant.id];
        return next;
      });
      if (staged) {
        // Header logo reads AuthProvider /auth/tenants (outside admin SSE) — refetch it.
        try {
          await refreshAvailableTenants();
        } catch {
          // Best-effort Header sync; the save itself already succeeded.
        }
      }
    } catch (saveError: unknown) {
      setError(saveError instanceof Error ? saveError.message : 'שמירת פרטי החברה נכשלה');
    } finally {
      panelSaveLocksRef.current.delete(tenant.id);
      setUpdatingTenantIds((previous) => removeUpdatingTenantId(previous, tenant.id));
    }
  };

  return {
    tenants,
    isLoading,
    companyName,
    isCreating,
    updatingTenantIds,
    isCreateOpen,
    expandedTenantId,
    displayedError,
    tenantsCountLabel,
    setCompanyName,
    setIsCreateOpen,
    handleCreateTenant,
    toggleTenantActive,
    openCreateModal,
    onToggleExpanded,
    getTenantOwnerEmailDraft,
    setTenantOwnerEmailDraft,
    getStagedPhotoFile,
    getTenantPhotoPreviewUrl,
    showRemovePhotoControl,
    hasTenantPanelChanges,
    stageTenantPhoto,
    stageRemoveTenantPhoto,
    saveTenantPanel,
  };
}
